import { useMemo } from 'react';
import { Button, ButtonVariant } from '../../elements/Button';
import { ImportLocationsHeader } from './ImportLocationsHeader';
import { Table } from '../Table';
import {
  columns,
  generateTableRows,
} from '../../../helper/locationTableDetails';
import {
  CheckExistingLocationResponse,
  Location,
} from '../../../types/location';
import { IRow } from '../../../types/table';
import { ErrorInterface } from '../../../types/errors';
import UploadErrors from './UploadErrors';
import {
  ImportPhase,
  LocationImportStatisticsWithTitle,
} from './LocationImportStatisticsWithTitle';
import { get } from 'lodash';

interface LocationsPreviewProps {
  uploadedLocations: Location[];
  locationsCount: number;
  locationsReport: CheckExistingLocationResponse | null;
  uploadErrors?: ErrorInterface[];
  onCancel: () => void;
  onCreate: () => void;
  onUploadNewCSV: () => void;
}

const LocationsPreview = ({
  uploadedLocations,
  locationsCount,
  locationsReport,
  uploadErrors,
  onCancel,
  onCreate,
  onUploadNewCSV,
}: LocationsPreviewProps) => {
  const dataElements: IRow = useMemo(() => {
    return generateTableRows(uploadedLocations);
  }, [uploadedLocations]);

  const generateButtonText = (): string => {
    if (locationsReport) {
      const { existing_locations, new_locations } = locationsReport;

      if (get(existing_locations, 'length', 0) > 0) {
        return get(new_locations, 'length', 0) > 0
          ? 'Create and Update Locations'
          : 'Update Locations';
      }
    }

    return 'Create Locations';
  };

  return (
    <div className='flex flex-col font-sans w-full h-full justify-between items-center'>
      <ImportLocationsHeader />
      <div className='p-4 w-full'>
        {uploadErrors ? (
          <>
            <LocationImportStatisticsWithTitle
              title={'Errors'}
              uploadedLocationsCount={locationsCount}
              locationsReport={null}
              errors={uploadErrors}
              stateOfImport={ImportPhase.START}
            />
            <UploadErrors errors={uploadErrors} />
          </>
        ) : (
          <>
            <LocationImportStatisticsWithTitle
              title={'Preview'}
              uploadedLocationsCount={locationsCount}
              locationsReport={locationsReport}
              stateOfImport={ImportPhase.START}
            />

            <Table data={dataElements} columns={columns} />
          </>
        )}
      </div>
      <div className='w-full flex p-4 pt-0 justify-between'>
        <Button
          label={uploadErrors ? 'Close' : 'Cancel'}
          onClick={onCancel}
          variant={ButtonVariant.Ghost}
        />
        {uploadErrors ? (
          <Button
            variant={ButtonVariant.Default}
            label={'Upload New CSV'}
            className='w-[150px]'
            onClick={onUploadNewCSV}
          />
        ) : (
          <Button
            variant={ButtonVariant.Default}
            label={generateButtonText()}
            className='min-w-[150px]'
            onClick={onCreate}
          />
        )}
      </div>
    </div>
  );
};

export default LocationsPreview;
